/**
 * Migrate Flows Prompt
 *
 * Helps users migrate flows between environments, update connection references,
 * and handle environment-specific configurations.
 */

import type { Prompt, PromptMessage } from "@modelcontextprotocol/sdk/types.js";

// ============================================================================
// Prompt Definition
// ============================================================================

export const migrateFlowsPrompt: Prompt = {
  name: "migrate-flows",
  description:
    "Migrate Power Automate flows between environments. Handles connection mapping, environment variables, and validation.",
  arguments: [
    {
      name: "flowId",
      description: "The flow ID to migrate",
      required: true,
    },
    {
      name: "sourceEnvironment",
      description: "Source environment ID or name",
      required: true,
    },
    {
      name: "targetEnvironment",
      description: "Target environment ID or name",
      required: true,
    },
    {
      name: "connectionMapping",
      description: "JSON mapping of source connection refs to target connection names (optional)",
      required: false,
    },
    {
      name: "newDisplayName",
      description: "New display name for the migrated flow (optional)",
      required: false,
    },
  ],
};

// ============================================================================
// Prompt Message Generation
// ============================================================================

export interface MigrateFlowsArgs {
  flowId: string;
  sourceEnvironment: string;
  targetEnvironment: string;
  connectionMapping?: string;
  newDisplayName?: string;
}

export function generateMigrateFlowsMessages(args: MigrateFlowsArgs): PromptMessage[] {
  const systemMessage = `You are a Power Automate migration specialist. Your role is to help users safely migrate flows between environments.

## Migration Process

### 1. Pre-Migration Analysis
- Retrieve the source flow definition
- Identify all connection references
- List required connectors and their tiers
- Check for environment-specific configurations
- Validate the flow definition

### 2. Connection Mapping
Each connection reference in the flow needs a corresponding connection in the target environment:
\`\`\`json
{
  "shared_sharepointonline": "target-sharepoint-connection",
  "shared_office365": "target-outlook-connection"
}
\`\`\`

### 3. Environment Variables
Flows may reference environment variables that need to exist in target:
- List URLs, IDs, or configuration values
- Identify which values need updating
- Suggest environment variable creation

### 4. Migration Execution
- Clone the flow using clone_flow
- Update connection references
- Validate in target environment
- Test with a manual run if possible

### 5. Post-Migration Verification
- Verify all connections are valid
- Check flow state (Started/Stopped)
- Validate against the new environment

## Common Migration Issues

### Connection Reference Mismatch
- Source and target use different connection naming
- Solution: Create connection mapping document

### Missing Connectors
- Custom connectors not available in target
- Solution: Export and import custom connectors first

### Environment Variables
- Flow references env vars that don't exist
- Solution: Create matching env vars in target

### SharePoint Site URLs
- Hardcoded URLs that differ between environments
- Solution: Use environment variables for URLs

### User/Group IDs
- Microsoft Entra IDs hardcoded in flow
- Solution: Parameterize or use lookup actions

### Dataverse Tables
- Table names/schemas differ between environments
- Solution: Validate schema compatibility

## Best Practices

1. **Always validate before deploying**
   - Use validate_flow on the migrated definition
   - Check for expression errors
   - Verify all connections are mapped

2. **Document the migration**
   - Keep a mapping of source to target connections
   - Record any manual configuration needed
   - Note environment-specific changes

3. **Test incrementally**
   - Start with the flow in Stopped state
   - Run a single test execution
   - Monitor for unexpected behavior

4. **Handle failures gracefully**
   - Keep the source flow unchanged
   - Create new flow in target (don't overwrite)
   - Enable only after validation

5. **Consider dependencies**
   - Child flows must be migrated first
   - Shared connections may need separate setup
   - Environment variables must exist

## Tools to Use
- \`get_flow\`: Get source flow definition
- \`clone_flow\`: Clone to target environment
- \`validate_flow\`: Validate the migrated flow
- \`update_flow\`: Update connection references
- \`run_flow\`: Test the migrated flow`;

  const userPrompt = buildMigrationPrompt(args);

  return [
    {
      role: "user",
      content: {
        type: "text",
        text: systemMessage,
      },
    },
    {
      role: "user",
      content: {
        type: "text",
        text: userPrompt,
      },
    },
  ];
}

function buildMigrationPrompt(args: MigrateFlowsArgs): string {
  const parts: string[] = [
    `## Migration Request`,
    ``,
    `**Flow ID:** ${args.flowId}`,
    `**Source Environment:** ${args.sourceEnvironment}`,
    `**Target Environment:** ${args.targetEnvironment}`,
  ];

  if (args.newDisplayName) {
    parts.push(`**New Display Name:** ${args.newDisplayName}`);
  }

  if (args.connectionMapping) {
    parts.push(``);
    parts.push(`**Connection Mapping:**`);
    parts.push(`\`\`\`json`);
    parts.push(args.connectionMapping);
    parts.push(`\`\`\``);
  }

  parts.push(``);
  parts.push(`Please help me migrate this flow:`);
  parts.push(`1. Get the source flow definition from ${args.sourceEnvironment}`);
  parts.push(`2. Analyze connection references and required connectors`);
  parts.push(`3. Validate the flow definition for migration compatibility`);
  parts.push(`4. Identify any environment-specific values that need updating`);
  parts.push(`5. Provide the migration steps and commands to execute`);
  parts.push(`6. Suggest post-migration validation steps`);

  return parts.join("\n");
}

// ============================================================================
// Migration Helpers
// ============================================================================

export interface ConnectionMigrationPlan {
  sourceRef: string;
  connectorId: string;
  connectorName: string;
  tier: string;
  targetConnection: string | null;
  status: "mapped" | "missing" | "needs-mapping";
}

export interface MigrationAnalysis {
  flowId: string;
  flowName: string;
  sourceEnvironment: string;
  targetEnvironment: string;
  connectionPlan: ConnectionMigrationPlan[];
  environmentVariables: string[];
  warnings: string[];
  readyToMigrate: boolean;
}

/**
 * Parse a connection mapping string into an object
 */
export function parseConnectionMapping(
  mappingStr: string
): Record<string, string> | null {
  try {
    return JSON.parse(mappingStr) as Record<string, string>;
  } catch {
    return null;
  }
}

/**
 * Get migration checklist
 */
export function getMigrationChecklist(): string[] {
  return [
    "Verify target environment exists and you have access",
    "Ensure required connectors are available in target",
    "Create connections in target environment",
    "Prepare connection mapping document",
    "Check for environment-specific URLs or IDs",
    "Create environment variables in target if needed",
    "Validate flow definition before migration",
    "Clone flow to target environment",
    "Update connection references",
    "Validate migrated flow",
    "Test with manual trigger (if applicable)",
    "Enable flow once validated",
    "Document migration for future reference",
  ];
}

/**
 * Common connectors that need migration attention
 */
export const connectorMigrationNotes: Record<
  string,
  { notes: string; requiresConfig: boolean }
> = {
  shared_sharepointonline: {
    notes: "SharePoint site URLs may differ between environments. Use environment variables.",
    requiresConfig: true,
  },
  shared_commondataserviceforapps: {
    notes: "Dataverse tables must exist with matching schema in target environment.",
    requiresConfig: true,
  },
  shared_sql: {
    notes: "SQL Server connection strings differ between environments.",
    requiresConfig: true,
  },
  shared_keyvault: {
    notes: "Key Vault names and secrets must exist in target subscription.",
    requiresConfig: true,
  },
  shared_azureblob: {
    notes: "Storage account names differ between environments.",
    requiresConfig: true,
  },
};
